import React, { useState, useEffect, useCallback } from 'react';
import './AddOnsSelection.css';
import api from '../api/apiClient';

interface ServiceAddOn {
  _id: string;
  name: string;
  description?: string;
  priceCents: number;
  durationMinutes: number;
  isActive: boolean;
  displayOrder: number;
}

interface SelectedAddOn {
  sourceAddOnId?: string;
  sourceType: 'predefined' | 'custom';
  name: string;
  description?: string;
  priceCents: number;
  durationMinutes: number;
}

interface AddOnsSelectionProps {
  serviceId: string;
  serviceName: string;
  servicePrice: number;
  serviceCategory?: string;
  onAddOnsSelected: (addOns: SelectedAddOn[]) => void;
  onClose: () => void;
  existingAddOns?: SelectedAddOn[];
}

const AddOnsSelection: React.FC<AddOnsSelectionProps> = ({
  serviceId,
  serviceName,
  servicePrice,
  serviceCategory,
  onAddOnsSelected,
  onClose,
  existingAddOns = [],
}) => {
  const [predefinedAddOns, setPredefinedAddOns] = useState<ServiceAddOn[]>([]);
  const [selectedAddOns, setSelectedAddOns] = useState<SelectedAddOn[]>(existingAddOns);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPredefinedAddOns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First try to get service-specific add-ons
      let response = await api.get(`/stylist-services/${serviceId}/add-ons`);
      let addOns = response.data.data || [];
      
      // If no service-specific add-ons, fetch templates filtered by category
      if (addOns.length === 0 && serviceCategory) {
        console.log('[AddOnsSelection] No service add-ons found, fetching templates for category:', serviceCategory);
        try {
          const templatesResponse = await api.get(`/add-on-templates/category/${serviceCategory}`);
          const templates = templatesResponse.data.data || [];
          
          // Convert templates to add-on format (use suggested min price)
          addOns = templates.map((template: any) => ({
            _id: template._id,
            name: template.name,
            description: template.description,
            priceCents: template.suggestedPrice?.min || 0,
            durationMinutes: template.suggestedDuration || 0,
            isActive: true,
            isTemplate: true, // Mark as template
          }));
          console.log(`[AddOnsSelection] Loaded ${addOns.length} templates for category: ${serviceCategory}`);
        } catch (templateErr) {
          console.error('[AddOnsSelection] Error fetching templates:', templateErr);
        }
      }
      
      setPredefinedAddOns(addOns);
    } catch (err: any) {
      console.error('[AddOnsSelection] Error fetching add-ons:', err);
      setError('Failed to load add-ons.');
      setPredefinedAddOns([]);
    } finally {
      setLoading(false);
    }
  }, [serviceId, serviceCategory]);

  useEffect(() => {
    fetchPredefinedAddOns();
  }, [fetchPredefinedAddOns]);

  const isAddOnSelected = (addOnId: string): boolean => {
    return selectedAddOns.some(addon => addon.sourceAddOnId === addOnId);
  };

  const togglePredefinedAddOn = (addOn: ServiceAddOn) => {
    if (isAddOnSelected(addOn._id)) {
      setSelectedAddOns(prev => prev.filter(a => a.sourceAddOnId !== addOn._id));
    } else {
      const newAddOn: SelectedAddOn = {
        sourceAddOnId: addOn._id,
        sourceType: 'predefined',
        name: addOn.name,
        description: addOn.description,
        priceCents: addOn.priceCents,
        durationMinutes: addOn.durationMinutes,
      };
      setSelectedAddOns(prev => [...prev, newAddOn]);
    }
  };

  const removeSelectedAddOn = (index: number) => {
    setSelectedAddOns(prev => prev.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const addOnsTotal = selectedAddOns.reduce((sum, addon) => sum + addon.priceCents, 0) / 100;
    const addOnsDuration = selectedAddOns.reduce((sum, addon) => sum + (addon.durationMinutes || 0), 0);
    const totalPrice = servicePrice + addOnsTotal;
    return { addOnsTotal, addOnsDuration, totalPrice };
  };

  const handleSave = () => {
    onAddOnsSelected(selectedAddOns);
    onClose();
  };

  const { addOnsTotal, addOnsDuration, totalPrice } = calculateTotals();

  return (
    <div className="addons-modal-overlay" onClick={onClose}>
      <div className="addons-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="addons-header">
          <h2>Add-ons for {serviceName}</h2>
          <button className="addons-close-btn" onClick={onClose}>
            <span>&times;</span>
          </button>
        </div>

        <div className="addons-body">
          {/* Service Info */}
          <div className="addons-service-info">
            <span>Base Service</span>
            <strong>${servicePrice.toFixed(2)}</strong>
          </div>

          {loading ? (
            <div className="addons-loading">
              <div className="spinner"></div>
              <p>Loading add-ons...</p>
            </div>
          ) : (
            <>
              {/* Error message */}
              {error && (
                <div className="addons-error">
                  <span className="error-icon">⚠️</span>
                  <p>{error}</p>
                </div>
              )}

              {/* Predefined Add-ons */}
              {predefinedAddOns.length > 0 && (
                <div className="addons-section">
                  <h3>Available Add-ons</h3>
                  <div className="addons-list">
                    {predefinedAddOns.map((addOn) => (
                      <div
                        key={addOn._id}
                        className={`addon-card ${isAddOnSelected(addOn._id) ? 'selected' : ''}`}
                        onClick={() => togglePredefinedAddOn(addOn)}
                      >
                        <div className="addon-checkbox">
                          <input
                            type="checkbox"
                            checked={isAddOnSelected(addOn._id)}
                            onChange={() => {}}
                          />
                        </div>
                        <div className="addon-info">
                          <h4>{addOn.name}</h4>
                          {addOn.description && <p className="addon-description">{addOn.description}</p>}
                          {addOn.durationMinutes > 0 && (
                            <p className="addon-duration">+{addOn.durationMinutes} min</p>
                          )}
                        </div>
                        <div className="addon-price">${(addOn.priceCents / 100).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Add-ons Summary */}
              {selectedAddOns.length > 0 && (
                <div className="addons-section">
                  <h3>Selected Add-ons ({selectedAddOns.length})</h3>
                  <div className="selected-addons-list">
                    {selectedAddOns.map((addOn, index) => (
                      <div key={index} className="selected-addon-item">
                        <div className="selected-addon-info">
                          <span className="selected-addon-name">{addOn.name}</span>
                          <span className="selected-addon-price">
                            ${(addOn.priceCents / 100).toFixed(2)}
                          </span>
                        </div>
                        <button
                          className="remove-addon-btn"
                          onClick={() => removeSelectedAddOn(index)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Price Breakdown */}
              <div className="addons-price-breakdown">
                <div className="price-row">
                  <span>Service</span>
                  <span>${servicePrice.toFixed(2)}</span>
                </div>
                {selectedAddOns.length > 0 && (
                  <>
                    {addOnsTotal > 0 && (
                      <div className="price-row">
                        <span>Add-ons ({selectedAddOns.length})</span>
                        <span>${addOnsTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {addOnsDuration > 0 && (
                      <div className="price-row small">
                        <span>Additional duration</span>
                        <span>+{addOnsDuration} min</span>
                      </div>
                    )}
                  </>
                )}
                <div className="price-divider"></div>
                <div className="price-row total">
                  <span>Total Now</span>
                  <strong>${totalPrice.toFixed(2)}</strong>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="addons-footer">
          <button className="addons-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="addons-save-btn" onClick={handleSave}>
            {selectedAddOns.length > 0 ? 'Save Changes' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddOnsSelection;
